import { useCallback, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { noop, difference } from 'lodash'
import { Link } from 'react-router-dom'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { Stack } from '@mui/material'

import { useGetFreightBillsQuery } from 'services/bills'

import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import Chip from 'components/generic/chip/Chip'

import { setDecimalPrecision, buildFilter } from 'utils/helpers'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import useRenderDateRangePicker from 'utils/custom-hooks/useRenderDateRangePicker'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { SORTABLE_FREIGHT_BILL_FIELDS, PAGE_SIZE, CURRENCY_PREPENDS } from 'utils/constants'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import DialogModal from '.'

const FreightBillsModal = ({
  onAdd,
  onClose,
  selectedInboundList,
  selectedOutboundList,
  billId,
  currentCarrier,
  getPaymentTerms,
  ...restProps
}) => {
  const apiRef = useGridApiRef()

  const { renderDateRangePicker, startDate, endDate } = useRenderDateRangePicker()
  const { getDueDateFormatted } = usePaymentTerms({})

  const { currencyExchangeRatesFF, enableLockAccountingV2 } = useAppSelector(
    state => state.featureFlags?.variables || {},
  )
  const { defaultYardTimezone } = useAppSelector(state => state.auth)
  const { settings, defaultCurrency } = useAppSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const [carrierFilter, setCarrierFilter] = useState({ name: currentCarrier?.displayName, id: currentCarrier?.id })
  const [selectedFreightBillIds, setSelectedFreightBillIds] = useState([
    ...selectedInboundList,
    ...selectedOutboundList,
  ])
  const [selectedInboundFreightBillIds, setSelectedInboundFreightBillIds] = useState(selectedInboundList)
  const [selectedOutboundFreightBillIds, setSelectedOutboundFreightBillIds] = useState(selectedOutboundList)

  const [filter, setFilter] = useState({
    sort: {
      field: SORTABLE_FREIGHT_BILL_FIELDS.CARRIER_NAME,
      ascending: true,
    },
    search: '',
    ...JSON.parse(buildFilter(startDate, endDate, defaultYardTimezone)),
  })

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      date: undefined, // clear start date before applying buildFilter
      endDate: undefined, // clear end date before applying buildFilter
      ...JSON.parse(buildFilter(startDate, endDate, defaultYardTimezone)),
    }))
  }, [startDate, endDate, defaultYardTimezone])

  const [paginationOptions, setPaginationOptions] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data: freightBillsList, isFetching } = useGetFreightBillsQuery(
    {
      filter: JSON.stringify({
        ...filter,
        carrierId: carrierFilter?.id || undefined,
        billId: billId || undefined,
      }),
      ...paginationOptions,
    },
    { skip: !filter?.sort?.field },
  )

  const transformedRows = (freightBillsList?.Items || []).slice().map(item => {
    const freightBillCurrency = item?.currency
    const freightBillPricePrepend =
      !freightBillCurrency || defaultCurrency === freightBillCurrency
        ? CURRENCY_PREPENDS[defaultCurrency]?.hc
        : CURRENCY_PREPENDS[freightBillCurrency]?.fc
    return {
      id: item.ticketId,
      ticketType: item.ticketType,
      ticketNumber: item.ticketNumber,
      effectiveDate: dateFormatter(item.effectiveDate),
      carrierId: item.carrierId,
      carrierName: item.carrierName,
      dueDate: getDueDateFormatted(item.effectiveDate, getPaymentTerms(item)),
      freightCost: setDecimalPrecision(item?.freightCost, {
        shouldFormatNumber: false,
        decimalPrecisionOverride: 2,
      }),
      pricePrepend: currencyExchangeRatesFF ? freightBillPricePrepend : '$',
    }
  })

  const freightBillColumns = useMemo(
    () => [
      {
        field: 'ticketNumber',
        headerName: 'Ticket #',
        width: 80,
        sortable: true,
        renderCell: params => {
          const { id, ticketNumber, ticketType } = params.row
          return (
            <Link
              to={`/app/${ticketType === 'IBT' ? 'purchases/ticket-library' : 'sales/outbound-tickets'}/${id}`}
              target="_blank"
            >
              {ticketNumber}
            </Link>
          )
        },
      },
      {
        field: 'carrierName',
        headerName: 'Carrier',
        flex: 1,
        sortable: true,
      },
      {
        field: 'freightCost',
        headerName: 'Freight Cost',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { freightCost, pricePrepend = '$' } = params.row
          return `${pricePrepend}${setDecimalPrecision(freightCost || 0, { decimalPrecisionOverride: 2 })}`
        },
        sortable: false,
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        flex: 1,
        sortable: false,
      },
    ],
    [],
  )

  const handleSortModelChange = useCallback(sortModel => {
    setFilter(prevFilter => ({
      ...prevFilter,
      sort: {
        field: sortModel?.[0]?.field,
        ascending: sortModel?.[0]?.sort === 'asc',
      },
    }))
  }, [])

  // In addition to handling freight bill selection, this handleSelectionModelChange function also handles adding "temporary" filters
  // These temporary filters are only relevant if there is no carrier selected on the bill before the user opens the modal (eg. currentCarrier)
  // The purpose of these temporary filters is to prevent users from mixing carriers in the same bill

  const handleSelectionModelChange = selectionModel => {
    const freightBillWasAdded = selectionModel.length > selectedFreightBillIds.length

    if (freightBillWasAdded) {
      const newIds = difference(selectionModel, selectedFreightBillIds)
      const newInboundFreightBillIds = []
      const newOutboundFreightBillIds = []
      newIds.forEach((newId, index) => {
        const addedFreightBill = transformedRows.find(freightBill => freightBill.id === newId)
        if (addedFreightBill) {
          if (index === 0) {
            // after a freight bill is selected, make sure we only show freight bills belonging to that same carrier
            setCarrierFilter({ name: addedFreightBill?.carrierName, id: addedFreightBill?.carrierId })
          }

          if (addedFreightBill.ticketType === 'IBT') {
            newInboundFreightBillIds.push(newId)
          } else if (addedFreightBill.ticketType == 'OBT') {
            newOutboundFreightBillIds.push(newId)
          }
        }
      })
      setSelectedInboundFreightBillIds(prev => [...prev, ...newInboundFreightBillIds])
      setSelectedOutboundFreightBillIds(prev => [...prev, ...newOutboundFreightBillIds])
    } else {
      const removedIds = difference(selectedFreightBillIds, selectionModel)
      const removedInboundFreightBillIds = []
      const removedOutboundFreightBillIds = []

      if (selectionModel.length === 0) {
        // if all freight bills are removed, revert back to currentCarrier (which could be null)
        setCarrierFilter({ name: currentCarrier?.displayName, id: currentCarrier?.id })

        // clear selected inbound and outbound ids
        setSelectedInboundFreightBillIds([])
        setSelectedOutboundFreightBillIds([])
      } else {
        removedIds.forEach(removedId => {
          const removedFreightBill = transformedRows.find(freightBill => freightBill.id === removedId)
          if (removedFreightBill) {
            if (removedFreightBill.ticketType === 'IBT') {
              removedInboundFreightBillIds.push(removedId)
            } else if (removedFreightBill.ticketType === 'OBT') {
              removedOutboundFreightBillIds.push(removedId)
            }
          }
        })

        setSelectedInboundFreightBillIds(prev => difference(prev, removedInboundFreightBillIds))
        setSelectedOutboundFreightBillIds(prev => difference(prev, removedOutboundFreightBillIds))
      }
    }
    setSelectedFreightBillIds(selectionModel)
  }

  return (
    <DialogModal
      {...restProps}
      onClose={() => {
        onClose()
      }}
      onSave={() => onAdd(selectedInboundFreightBillIds, selectedOutboundFreightBillIds)}
    >
      <Stack sx={{ height: 600 }}>
        <DataGridWithSearchV2
          density="compact"
          height="auto"
          rows={transformedRows}
          columns={freightBillColumns}
          apiRef={apiRef}
          useBackendSortAndFilter
          renderDateRangePicker={renderDateRangePicker}
          filter={filter}
          setFilter={setFilter}
          checkboxSelection
          disableColumnMenu
          loading={isFetching}
          showFilterChips
          filterChips={<>{carrierFilter?.id && <Chip label={`Carrier: ${carrierFilter?.name}`} />}</>}
          initialState={{
            sorting: {
              sortModel: [{ field: freightBillColumns[1].field, sort: 'asc' }],
            },
            selection: {
              selectionModel: [...selectedInboundList, ...selectedOutboundList],
            },
          }}
          // pagination
          rowCount={freightBillsList?.totalResultsCount || 0}
          paginationMode="server"
          onPageChange={newPageNumber => setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
          onPageSizeChange={newPageSize => setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
          autoPageSize={false}
          pageSize={paginationOptions.pageSize}
          // sorting
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          onSortModelChange={handleSortModelChange}
          // selection
          keepNonExistentRowsSelected
          selectionModel={selectedFreightBillIds}
          onSelectionModelChange={handleSelectionModelChange}
          isRowSelectable={params => {
            if (enableLockAccountingV2) return true // if lock accounting V2 is turned on, we do not need to disable any fields
            if (selectedFreightBillIds.indexOf(params.id) > -1) return true // allow users to select/unselect tickets that are already linked to the bill
            if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return true
            const freightBillIsLocked =
              new Date(params.row?.effectiveDate).getTime() <
              new Date(lockAccountingPeriod.lockAccountingDate).getTime()
            return !freightBillIsLocked
          }}
        />
      </Stack>
    </DialogModal>
  )
}

FreightBillsModal.propTypes = {
  ...DialogModal.propTypes,
  selectedInboundList: PropTypes.arrayOf(PropTypes.string),
  selectedOutboundList: PropTypes.arrayOf(PropTypes.string),
  billId: PropTypes.string,
  currentCarrier: PropTypes.object,
  getPaymentTerms: PropTypes.func,
}

FreightBillsModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Import Tickets to Payment',
  selectedInboundList: [],
  selectedOutboundList: [],
  billId: null,
  currentCarrier: null,
  getPaymentTerms: noop,
}

export default FreightBillsModal
